import { Collapsible, useCollapsible } from '@/components/shared/Collapsible';
import chevron from '@/static/assets/chevron.png';
import { Flex, font, preventOneWordInLastLine, themedColor } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis-atomic';
import parse from 'html-react-parser';
import Image from 'next/image';
import type React from 'react';
import { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

const StyledCollapsibleBox = styled.div`
  padding: 20px;
  border-radius: 10px;
  width: 100%;
  background-color: ${themedColor('white')};
  box-shadow: 0px 4px 25px rgba(0, 0, 0, 0.1);
  height: auto;
  transition: all 0.2s ease;
  cursor: pointer;

  ${MEDIA_QUERIES.desktop`
    padding: 30px;
    cursor: text;
  `}
`;

export const CollapsibleBoxContainer = styled.div<{
  /**
   * will apply the same min height to all boxes in grid mode, default is 'auto', pass the value with 'px' or 'rem'
   */
  minBoxesHeight?: string;
  variant?: 'default' | 'gray';
}>`
  display: grid;
  grid-template-columns: 1fr;
  gap: 15px;
  align-items: stretch;
  text-align: left;

  ${MEDIA_QUERIES.desktop`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 30px;

    ${StyledCollapsibleBox} {
      width: calc((100% - 60px) / 3);
      min-height: ${({ minBoxesHeight }) => minBoxesHeight ?? 'auto'};
    }

    ${({ variant }) =>
      variant !== 'gray' &&
      `
      &:has(${StyledCollapsibleBox}:hover) ${StyledCollapsibleBox}:not(:hover) {
        opacity: 0.5;
      }

      ${StyledCollapsibleBox}:hover {
        transform: scale(1.08);
      }
    `}
  `}

  ${({ variant }) =>
    variant === 'gray' &&
    css`
      ${StyledCollapsibleBox} {
        background-color: #f7f7f7;
        box-shadow: none;
      }

      ${CollapsibleBoxTitle} {
        line-height: 2.3rem;
      }

      ${MEDIA_QUERIES.desktop`
        ${CollapsibleBoxTitle} {
         line-height: 145%;
      }
      `}

      @media (min-width: 990px) {
        gap: 24px;

        ${StyledCollapsibleBox} {
          width: 364px;
          height: 364px;
          min-height: 364px;
        }
      }
    `}
`;

const CollapsibleBoxTitle = styled.h3`
  ${font('main', {
    fontSize: '1.6rem',
    lineHeight: '1.6rem',
    fontWeight: 'bold',
  })}
  color: ${themedColor('night')};
  white-space: normal;

  ${MEDIA_QUERIES.desktop`
    ${font('main', {
      fontSize: '2.4rem',
      lineHeight: '2.6rem',
      fontWeight: 'bold',
    })}
    white-space: pre-line;
  `}
`;

const CollapsibleBoxContent = styled.p`
  ${font('main', { fontSize: '1.6rem', lineHeight: 1.4 })}
  color: ${themedColor('boulder')};
  margin-top: 20px;
  white-space: normal;
  text-wrap: balance;

  a {
    color: ${themedColor('primary')};
    text-decoration: underline !important;
    &:hover {
      text-decoration: none;
    }
  }

  ${MEDIA_QUERIES.tabletAndDesktop`
    white-space: pre-line;
  `}
`;

const StyledImage = styled(Image)`
  display: block;
  transition: transform 0.2s ease;
  pointer-events: none;
  ${MEDIA_QUERIES.desktop`
    display: none;
  `}

  &.open {
    transform: rotate(180deg);
  }

  &.closed {
    transform: rotate(0deg);
  }
`;

interface CollapsibleBoxProps extends React.ComponentPropsWithRef<'div'> {
  title: string;
  content: string;
}

export const CollapsibleBox: React.FC<CollapsibleBoxProps> = ({ title, content, onClick, ...restProps }) => {
  const { open, toggle } = useCollapsible();
  const [isDesktopViewport, setIsDesktopViewport] = useState(true);

  useEffect(() => {
    setIsDesktopViewport(window.innerWidth > 990);
    const handleResize = () => {
      setIsDesktopViewport(window.innerWidth > 990);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <StyledCollapsibleBox
      onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (!isDesktopViewport) toggle();
        onClick?.(e);
      }}
      {...restProps}
      tabIndex={0}
    >
      <Flex justifyContent="space-between" gap="20px" alignItems="center">
        <CollapsibleBoxTitle>{preventOneWordInLastLine(title)}</CollapsibleBoxTitle>

        <StyledImage src={chevron} height={8} width={15} alt="" className={open ? 'open' : 'closed'} />
      </Flex>

      <Collapsible open={isDesktopViewport ? true : open}>
        <CollapsibleBoxContent>{parse(content)}</CollapsibleBoxContent>
      </Collapsible>
    </StyledCollapsibleBox>
  );
};
